export default class Access {
  constructor(target, user) {
    this.user = user ?? { id: -1, accountBlocked: false }; // for not authenticated users
    switch (target['@type']) {
      case 'Project':
        this.project = target;
        break;
      case 'Task':
        this.task = target;
        this.project = target.project;
        break;
      default:
        throw new Error(`Unhandled entity type "${target['@type']}"`);
    }
  }

  findProjectMember(role = null) {
    const members = this.project?.projectMembers ?? [];
    return members.find((pm) => pm.user.id === this.user.id && (role === null || pm.role === role));
  }

  isCompanyAdmin(includeMembership = false) {
    const department = this.project?.companyDepartment;

    if (!department) return false;

    const company = department.company;

    const companies = [
      ...(this.user?.companies ?? []).map(c => c['@id']),
      ...(this.user?.employments ?? [])
        .map(e => (e.deputyships ?? []).map(c => c['@id']))
        .reduce((acc, val) => [...acc, ...val], [])
    ];

    if (companies.includes(company['@id'])) return true;

    const departments = (this.user?.employments ?? [])
      .map(
        e => (e.memberships ?? [])
          .filter(m => m.role === 'head')// || (includeMembership ? m.role === 'manager' : false))
          .map(m => m.department['@id'])
      )
      .reduce((acc, val) => [...acc, ...val], []);

    if (departments.includes(department['@id'])) return true;

    return false;
  }

  isProjectAuthor(isStrict) {
    const isRealAuthor = this.project?.author?.id === this.user.id;
    return isStrict ? isRealAuthor : isRealAuthor || this.isCompanyAdmin();
  }

  isProjectAdmin() {
    return !!this.findProjectMember('admin');
  }

  isProjectWorker() {
    return !!this.findProjectMember('work');
  }

  isProjectStandardMember() {
    return !!this.findProjectMember('standard');
  }

  isProjectMember() {
    return !!this.findProjectMember();
  }

  isTaskAuthor() {
    return this.task?.author?.id === this.user.id;
  }

  isTaskManager() {
    return this.task?.manager?.id === this.user.id;
  }

  isTaskMember() {
    const members = this.task?.members ?? [];
    return members.includes(this.user['@id']);
  }

  isAccountBlocked() {
    return this.project.author?.accountBlocked;
  }

  isProjectArchived() {
    return this.project.isArchived;
  }

  projectIsTemplate() {
    return this.project.isTemplate;
  }

  isProjectEditable() {
    return !this.isAccountBlocked() && !this.isProjectArchived();
  }

  checkProjectExists() {
    if (!this.project) {
      throw new Error('Access error: project not found');
    }
  }

  checkTaskExists() {
    if (!this.task) {
      throw new Error('Access error: task not found');
    }
  }

  checkUserExists() {
    if (!this.user) {
      throw new Error('Access error: user not found');
    }
  }

  canEditProject() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor() || this.isProjectAdmin();
  }

  canCreateTemplate() {
    this.checkProjectExists();
    this.checkUserExists();

    return this.isProjectAuthor() || this.isProjectAdmin() || this.isProjectWorker();
  }

  canCopyProject() {
    this.checkProjectExists();
    this.checkUserExists();

    return this.isProjectAuthor() || this.isProjectAdmin() || this.isProjectWorker();
  }

  canArchiveProject() {
    this.checkProjectExists();
    this.checkUserExists();

    return this.isProjectAuthor();
  }

  canUnarchiveProject() {
    this.checkProjectExists();
    this.checkUserExists();

    if (this.project.author.accountBlocked) {
      return false;
    }

    return this.isProjectAuthor();
  }

  canEditStages() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor()
      || this.isProjectAdmin()
      || (this.isProjectWorker() && !this.projectIsTemplate());
  }

  canCreateTask() {
    this.checkProjectExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    return this.isProjectAuthor()
      || this.isProjectAdmin()
      || (this.isProjectMember() && !this.projectIsTemplate());
  }

  canUpdateTask(fullAccess = false) {
    this.checkProjectExists();
    this.checkTaskExists();
    this.checkUserExists();

    if (!this.isProjectEditable()) {
      return false;
    }

    let hasAccess = this.isProjectAuthor()
      || this.isProjectAdmin()
      || (this.isTaskAuthor() && !this.projectIsTemplate());

    if (!fullAccess) {
      hasAccess ||= this.isTaskManager() && !this.projectIsTemplate();
    }
    return hasAccess;
  }
}
